/* Sheets the composer opens: proven posts, the listing photo picker, and an AI artwork prompt. */
import type { MediaDto } from '@str/api/types';
import { Overlay } from '@/components/Overlay';
import { useState } from 'react';
import { mediaStyle } from '@/lib/scenes';

export function LibrarySheet({
  photos,
  single,
  onClose,
  onAdd,
  onUpload,
}: {
  photos: MediaDto[];
  single: boolean;
  onClose: () => void;
  onAdd: (items: MediaDto[]) => void;
  onUpload: () => void;
}) {
  const [sel, setSel] = useState<string[]>([]);
  const usable = photos.filter((m) => !!m.url);
  const toggle = (id: string) =>
    setSel((s) =>
      single ? [id] : s.includes(id) ? s.filter((x) => x !== id) : s.length >= 10 ? s : [...s, id],
    );
  return (
    <Overlay className="nx-sheet-back" onClose={onClose}>
      <div
        className="nx-sheet wide"
        role="dialog"
        aria-label={single ? 'Choose a photo' : 'Your listing photos'}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>
          <span>
            {single ? 'Choose a photo' : 'Your listing photos'}{' '}
            <span style={{ fontSize: 12.5, fontWeight: 500, color: '#888', marginLeft: 8 }}>
              {usable.length} with a file we can use
            </span>
          </span>
          <button type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </h2>
        {usable.length === 0 && (
          <p className="nx-note">
            None of the listing photos have a file yet. Add photos from this device instead.
          </p>
        )}
        <div className="nx-lib">
          {usable.map((m) => (
            <button
              key={m.id}
              type="button"
              className={sel.includes(m.id) ? 'on' : ''}
              aria-pressed={sel.includes(m.id)}
              aria-label={m.label}
              title={m.label}
              style={mediaStyle(m)}
              onClick={() => toggle(m.id)}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="nx-chip" onClick={onUpload}>
            Add from this device instead
          </button>
          <span className="nx-note" style={{ marginLeft: 'auto' }}>
            {single ? '' : `${sel.length} chosen · up to 10`}
          </span>
          <button
            type="button"
            className="btn btn-primary hv"
            disabled={!sel.length}
            onClick={() => onAdd(usable.filter((m) => sel.includes(m.id)))}
          >
            {single ? 'Use this photo' : `Add ${sel.length || ''} →`}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export function ArtworkSheet({
  busy,
  onClose,
  onMake,
}: {
  busy: boolean;
  onClose: () => void;
  onMake: (prompt: string) => void;
}) {
  const [prompt, setPrompt] = useState('');
  return (
    <Overlay className="nx-sheet-back" onClose={onClose}>
      <div
        className="nx-sheet"
        role="dialog"
        aria-label="AI artwork"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>
          🎨 AI artwork{' '}
          <button type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </h2>
        <p className="nx-note">
          A picture made from scratch for an offer or the season. None of your photos are used, so
          it never shows your actual place. It is always marked AI.
        </p>
        <input
          value={prompt}
          aria-label="Describe the artwork"
          placeholder="e.g. Midweek 20% off"
          onChange={(e) => setPrompt(e.target.value)}
          style={{
            border: '1px solid rgba(0,0,0,.12)',
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 14,
          }}
        />
        <button
          type="button"
          className="btn btn-primary hv"
          disabled={busy || !prompt.trim()}
          onClick={() => onMake(prompt.trim())}
          style={{ alignSelf: 'flex-end' }}
        >
          Make it →
        </button>
      </div>
    </Overlay>
  );
}
